export interface CurrentNaviOptions {
    subSectionClass?: string;
    showLabels?: 'hover' | 'always' | 'active';
}

export class CurrentNavi {
    private sections: HTMLElement[];
    private naviWrapper: HTMLElement | null;
    private labelContainers: NodeListOf<HTMLElement>;
    private items: HTMLLIElement[];
    private currentIndex: number;
    private isScrolling: boolean;
    private scrollTimeout?: ReturnType<typeof setTimeout>;
    private options: CurrentNaviOptions;
    private observer: IntersectionObserver | null = null;

    /**
     * CSSセレクタ、または直接Node要素のリストを渡して初期化
     * @param targets - 対象となるセクションのCSSセレクタ、NodeList、配列、または単一要素
     * @param options - 動作オプション（サブセクションクラスの指定など）
     */
    constructor(
        targets: string | NodeListOf<HTMLElement> | HTMLElement[] | HTMLElement,
        options: CurrentNaviOptions = {}
    ) {
        this.options = {
            subSectionClass: 'sub-section', // デフォルトのサブセクション判定用クラス
            showLabels: 'hover',
            ...options
        };
        if (typeof targets === 'string') {
            this.sections = Array.from(document.querySelectorAll<HTMLElement>(targets));
        } else if (targets instanceof HTMLElement) {
            this.sections = [targets];
        } else {
            this.sections = Array.from(targets);
        }

        this.naviWrapper = document.querySelector<HTMLElement>('.current-navi');
        this.labelContainers = document.querySelectorAll<HTMLElement>('.current-navi-label');

        this.items = [];
        this.currentIndex = -1;
        this.isScrolling = false;

        if (!this.sections.length || !this.naviWrapper) return;

        if (this.options.showLabels === 'always') {
            this.naviWrapper.classList.add('show-labels');
        }

        this.init();
    }

    private init(): void {
        this.createNav();
        this.observe();
    }

    private createNav(): void {
        this.sections.forEach((section, index) => {
            const label = section.getAttribute('data-label');

            // ドットを生成
            const li = document.createElement('li');
            li.classList.add('current-navi-item');

            // サブセクションの場合は専用のモディファイアクラスを付与
            if (this.options.subSectionClass && section.classList.contains(this.options.subSectionClass)) {
                li.classList.add('is-sub-item');
            }

            // href ベースのリンク生成
            const a = document.createElement('a');
            if (section.id) a.href = `#${section.id}`;

            // ドットにtitle属性付与（クラス要素にdata-labelがあれば）
            if (label) {
                a.setAttribute('title', label);
            }

            li.appendChild(a);

            if (this.naviWrapper) {
                this.naviWrapper.appendChild(li);
                this.items.push(li);
            }
        });
    }

    private observe(): void {
        // 3. スクロールに応じてドットをアクティブに
        const observerOptions: IntersectionObserverInit = {
            root: null,
            rootMargin: '-50% 0px -50% 0px', // 画面の半分を交差した時に発火
            threshold: 0
        };

        this.observer = new IntersectionObserver((entries) => {
            if (this.isScrolling) return; // クリックによる移動中はスキップ

            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = this.sections.indexOf(entry.target as HTMLElement);
                    if (index !== -1) this.activate(index);
                }
            });
        }, observerOptions);

        this.sections.forEach(section => this.observer!.observe(section));
    }

    destroy(): void {
        this.observer?.disconnect();
        this.observer = null;
        this.items.forEach(item => item.remove());
        this.items = [];
    }

    private activate(index: number): void {
        if (this.currentIndex === index) return;

        // 進行方向の判定: indexが増える=スクロールダウン
        const direction: 'down' | 'up' = (index > this.currentIndex) ? 'down' : 'up';

        // セクションの表示状態クラスの切り替え (Demo向け演出)
        if (this.currentIndex !== -1) {
            this.sections[this.currentIndex].classList.remove('is-visible');
        }
        this.currentIndex = index;
        this.sections[this.currentIndex].classList.add('is-visible');

        // ドットのアクティブ状態の切り替え
        this.items.forEach((item, i) => {
            if (i === index) {
                item.classList.add('is-active');
                if (this.options.showLabels === 'active') {
                    item.classList.add('show-labels');
                }
            } else {
                item.classList.remove('is-active');
                if (this.options.showLabels === 'active') {
                    item.classList.remove('show-labels');
                }
            }
        });

        // ページ内リンク (a[href="#id"]) のアクティブ状態の切り替え
        const activeSection = this.sections[index];
        const activeId = activeSection.id;
        // サブセクションの場合は親 .section の ID をナビリンクのマッチに使う
        const navId = (this.options.subSectionClass && activeSection.classList.contains(this.options.subSectionClass))
            ? (activeSection.closest(`.section:not(.${this.options.subSectionClass})`) as HTMLElement | null)?.id ?? activeId
            : activeId;
        // .active-parent-links: 親セクションのIDのみにマッチ
        if (navId) {
            document.querySelectorAll<HTMLAnchorElement>('.active-parent-links a[href^="#"]').forEach(link => {
                const target = link.getAttribute('href')?.slice(1);
                link.classList.toggle('is-active', target === navId);
            });
        }

        // .active-links: 子(activeId)・親(navId)どちらもマッチ
        if (activeId || navId) {
            document.querySelectorAll<HTMLAnchorElement>('.active-links a[href^="#"]').forEach(link => {
                const target = link.getAttribute('href')?.slice(1);
                const isMatch = (activeId && target === activeId) || (navId && target === navId);
                link.classList.toggle('is-active', Boolean(isMatch));
            });
        }

        // 4. .current-navi-labelに現在のアクティブなラベルをセットし、アニメーション
        if (this.labelContainers.length > 0) {
            const section = this.sections[index];
            const newLabelText = section.getAttribute('data-label') || '';
            if (newLabelText) {
                this.labelContainers.forEach(container => {
                    this.updateLabel(container, newLabelText, direction);
                });
            }
        }
    }

    private updateLabel(container: HTMLElement, newText: string, direction: 'down' | 'up'): void {
        const oldLabels = container.querySelectorAll<HTMLElement>('.label-text');

        // 新しいラベルを生成
        const nextLabel = document.createElement('span');
        nextLabel.classList.add('label-text');
        nextLabel.textContent = newText;

        container.appendChild(nextLabel);

        // 次のフレームでアニメーションクラスを付与
        requestAnimationFrame(() => {
            // 古いラベルを退場させる
            oldLabels.forEach(label => {
                // 全てのアニメーションクラスとis-activeを一度削除
                label.classList.remove('active-up', 'active-down', 'is-active');

                // 方向に応じた退出クラス
                if (direction === 'down') {
                    label.classList.add('outgoing-up');
                } else {
                    label.classList.add('outgoing-down');
                }

                // アニメーション完了後にDOMから削除 (0.5s CSS animationに合わせて)
                setTimeout(() => label.remove(), 500);
            });

            // 新しいラベルを登場させる (方向に応じて上か下から)
            nextLabel.classList.add('is-active');
            if (direction === 'down') {
                nextLabel.classList.add('active-up');
            } else {
                nextLabel.classList.add('active-down');
            }
        });
    }
}
